/**
 * Component used to render a dumb thumb icon with an optional count
 *
 * @module components
 */

import values from 'lodash/values';
import PropTypes from 'prop-types';
import React from 'react';

import ThumbDownIcon from '../../../../static/icons/controls-thumb-down-filled.svg';
import ThumbUpIcon from '../../../../static/icons/controls-thumb-up-filled.svg';
import Icon from '../../../components/Icon/Icon';
import { THUMB_STYLES, THUMB_TYPES } from '../../../constants/Thumbs';
import { jeCSS } from '../../../lib/StyleUtils';

import './DisplayThumb.scss';

const THUMB_STYLE_VALUE_ARRAY = values(THUMB_STYLES);

export default class extends React.Component {
  /**
   * @property {string} thumbType - up or down?
   * @property {Bool} filled - display filled with color?
   * @property {func} onClick - called on click
   * @property {Bool} disabled - if true, prevent onClick on click
   * @property {number} count - thumb count to display. If not provided, no count shown
   * @property {string} styleName - BEModifier to give the class
   *    e.g. ['Dark'] adds .DisplayThumb--Dark
   *    will default to 'Light' (theme for light bg) if none specified
   */
  static propTypes = {
    thumbType: PropTypes.string.isRequired,
    filled: PropTypes.bool,
    onClick: PropTypes.func,
    disabled: PropTypes.bool,
    count: PropTypes.number,
    styleName: PropTypes.oneOf(THUMB_STYLE_VALUE_ARRAY),
  };

  static defaultProps = {
    thumbType: THUMB_TYPES.UP,
    styleName: THUMB_STYLES.LIGHT,
  };

  getButtonClasses = thumbType => {
    return jeCSS('DisplayThumb', [
      this.props.styleName,
      {
        up: thumbType === THUMB_TYPES.UP,
        down: thumbType === THUMB_TYPES.DOWN,
        active: this.props.filled,
        inactive: !this.props.filled,
        isClickable: !this.props.disabled && this.props.onClick,
      },
    ]);
  };

  render() {
    let maybeCount = null;
    if (this.props.count !== null) {
      maybeCount = <div className='DisplayThumb__flexWrap__count'>{this.props.count}</div>;
    }

    return (
      <button
        type='button'
        className={this.getButtonClasses(this.props.thumbType)}
        onClick={this.props.onClick}
        disabled={this.props.disabled}
      >
        <span className='visuallyHidden'>{'Thumb ' + this.props.thumbType} </span>
        <div className='DisplayThumb__flexWrap' aria-live='polite'>
          <Icon
            className='DisplayThumb__flexWrap__Icon'
            glyph={this.props.thumbType === THUMB_TYPES.UP ? ThumbUpIcon : ThumbDownIcon}
          />
          {maybeCount}
          <span className='visuallyHidden'>
            {this.props.thumbType === THUMB_TYPES.UP ? 'likes' : 'dislikes'}
          </span>
        </div>
      </button>
    );
  }
}
